import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { PropertyCard } from "@/components/PropertyCard";
import { SEO } from "@/components/SEO";
import { api, formatPrice, formatApiError, COUNTRY_FLAGS, COUNTRY_LABELS } from "@/lib/api";
import { BadgeCheck, Building2, Mail, Phone, MapPin, Home, TrendingUp, MessageCircle, Calendar } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = { vente: "À vendre", location: "À louer" };

const formatPhoneForWhatsApp = (raw) => {
  if (!raw) return null;
  const cleaned = String(raw).replace(/[^\d]/g, "");
  return cleaned || null;
};

const formatMemberSince = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

export default function Agency() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    (async () => {
      try {
        const { data } = await api.get(`/agencies/${slug}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (cancelled) return;
        const msg = formatApiError(e);
        setError(msg);
        if (e.response?.status !== 404) toast.error(msg);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (error) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-6 py-24 text-center" data-testid="agency-not-found">
          <Building2 className="w-12 h-12 text-[#E5E5E5] mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-medium mb-2">Agence introuvable</h1>
          <p className="text-muted-foreground mb-6">Cette agence n'existe pas ou n'est plus disponible.</p>
          <Link to="/search" className="text-[#D6202C] underline">Découvrir nos annonces</Link>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="h-48 rounded-3xl bg-[#F5F0EB] animate-pulse mb-8" data-testid="agency-loading" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[0,1,2,3].map(i => <div key={i} className="h-24 rounded-2xl bg-[#F5F0EB] animate-pulse" />)}
          </div>
        </div>
      </Layout>
    );
  }

  const { agency, stats, listings } = data;
  const wa = formatPhoneForWhatsApp(agency.phone);

  return (
    <Layout>
      <SEO
        title={`${agency.company || agency.name} · Annonces immobilières · 3aqarate`}
        description={`Découvrez les ${stats.total_listings} annonces de ${agency.company || agency.name} sur 3aqarate · ${COUNTRY_LABELS[agency.country]}`}
        image={agency.picture}
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#11783A] to-[#0d5e2d] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 md:py-16">
          <div className="flex flex-col md:flex-row md:items-end gap-6">
            <div
              className="w-28 h-28 md:w-36 md:h-36 rounded-2xl bg-white text-[#11783A] flex items-center justify-center font-heading text-5xl shadow-2xl shrink-0 overflow-hidden"
              data-testid="agency-avatar"
            >
              {agency.picture ? (
                <img src={agency.picture} alt={agency.name} className="w-full h-full object-cover" />
              ) : (
                (agency.company || agency.name).charAt(0)
              )}
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm text-white/70 mb-2">
                {COUNTRY_FLAGS[agency.country]} {COUNTRY_LABELS[agency.country]}
                {agency.member_since && (
                  <>
                    <span className="text-white/40">·</span>
                    <Calendar className="w-3.5 h-3.5" />
                    <span>Membre depuis {formatMemberSince(agency.member_since)}</span>
                  </>
                )}
              </div>
              <h1
                data-testid="agency-name"
                className="font-heading text-3xl md:text-5xl font-medium leading-tight mb-1"
              >
                {agency.company || agency.name}
              </h1>
              {agency.company && agency.company !== agency.name && (
                <div className="text-white/80 text-sm">par {agency.name}</div>
              )}
              {agency.badge === "pro_verified" && (
                <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs">
                  <BadgeCheck className="w-3.5 h-3.5 text-[#F4B400]" /> Pro vérifiée par 3aqarate
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row md:flex-col gap-2">
              {wa && (
                <a
                  href={`https://wa.me/${wa}?text=${encodeURIComponent(`Bonjour ${agency.name}, je vous contacte depuis 3aqarate.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="agency-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 px-5 h-11 rounded-xl bg-[#25D366] hover:bg-[#1FB854] transition-colors font-medium text-sm shadow-lg"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              )}
              {agency.email && (
                <a
                  href={`mailto:${agency.email}`}
                  data-testid="agency-email-btn"
                  className="inline-flex items-center justify-center gap-2 px-5 h-11 rounded-xl bg-white/15 hover:bg-white/25 backdrop-blur transition-colors font-medium text-sm"
                >
                  <Mail className="w-4 h-4" /> Email
                </a>
              )}
              {agency.phone && !wa && (
                <a
                  href={`tel:${agency.phone}`}
                  className="inline-flex items-center justify-center gap-2 px-5 h-11 rounded-xl bg-white/15 hover:bg-white/25 backdrop-blur transition-colors font-medium text-sm"
                >
                  <Phone className="w-4 h-4" /> {agency.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 -mt-8 relative z-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12" data-testid="agency-stats">
          <StatCard icon={<Home className="w-5 h-5" />} label="Annonces actives" value={stats.total_listings} />
          <StatCard icon={<TrendingUp className="w-5 h-5" />} label="À vendre" value={stats.by_type?.vente || 0} />
          <StatCard icon={<MapPin className="w-5 h-5" />} label="À louer" value={stats.by_type?.location || 0} />
          <StatCard
            icon={<BadgeCheck className="w-5 h-5" />}
            label="Prix moyen"
            value={stats.avg_price ? formatPrice(stats.avg_price, listings[0]?.currency || "MAD", "vente") : "—"}
          />
        </div>

        {/* Listings */}
        <section className="pb-16">
          <h2 className="font-heading text-2xl md:text-3xl font-medium mb-6">
            Annonces de {agency.company || agency.name}
          </h2>

          {listings.length === 0 ? (
            <div className="text-center py-16 bg-[#F5F0EB] rounded-3xl">
              <Building2 className="w-10 h-10 text-[#D6202C]/40 mx-auto mb-3" />
              <p className="text-muted-foreground">Aucune annonce active pour le moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agency-listings">
              {listings.map((l) => (
                <PropertyCard key={l.id} listing={l} />
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

const StatCard = ({ icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-[#E5E5E5] p-4 shadow-sm">
    <div className="w-9 h-9 rounded-lg bg-[#FEF2F3] text-[#D6202C] flex items-center justify-center mb-2">
      {icon}
    </div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="font-heading font-semibold text-lg mt-0.5">{value}</div>
  </div>
);
